import * as affineTransformOperator from "@arcgis/core/geometry/operators/affineTransformOperator.js";
import * as geodeticLengthOperator from "@arcgis/core/geometry/operators/geodeticLengthOperator.js";
import * as lengthOperator from "@arcgis/core/geometry/operators/lengthOperator.js";
import Transformation from "@arcgis/core/geometry/operators/support/Transformation.js";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import LineSymbolMarker from "@arcgis/core/symbols/LineSymbolMarker";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import MapView from "@arcgis/core/views/MapView";
import { canUseGeodesic, computeTrueNorth } from "../../../utils/arcgisUtils";
import { Unit } from "./measurementEnums";

// Identifies the graphics that belong to one drawn angle, so an editor can replace all of them
// without having to hold on to the graphic objects it saw earlier
let nextMeasurementId = 1;
const createMeasurementId = () => `angle-${nextMeasurementId++}`;

const createLine = (vertices: number[][], mapview: MapView) => {
    const polyline = new Polyline({
        paths: [vertices],
        spatialReference: mapview.spatialReference,
    });
    return polyline;
};

export type AngleGraphics = {
    graphics: Array<Graphic>;
    measures: Array<{ key: string; value: string }>;
};

/**
 * Builds the graphics and the readable measures for an angle between two vertices, without
 * touching any layer. Drawing a new angle and editing a finished one both go through this.
 *
 * `pinnedStart` is remembered on the line graphic so an editor can tell whether the first vertex
 * belongs to a selected object and has to stay where it is.
 */
export const buildAngleGraphics = async (
    vertices: number[][],
    mapview: MapView,
    unit: string | null,
    pinnedStart: number[] | null = null,
    measurementId: string = createMeasurementId()
): Promise<AngleGraphics> => {
    const line = createLine(vertices, mapview);

    let lineLengthMeters: number = 0;
    let lineLengthNauticalMiles: number = 0;

    if (canUseGeodesic(mapview)) {
        if (!geodeticLengthOperator.isLoaded()) {
            await geodeticLengthOperator.load();
        }
        lineLengthMeters = geodeticLengthOperator.execute(line, { unit: "meters" });
        lineLengthNauticalMiles = geodeticLengthOperator.execute(line, { unit: "nautical-miles" });
    } else {
        lineLengthMeters = lengthOperator.execute(line, { unit: "meters" });
        lineLengthNauticalMiles = lengthOperator.execute(line, { unit: "nautical-miles" });
    }

    const lineGraphic = new Graphic({
        geometry: line,
        symbol: new SimpleLineSymbol({
            color: [4, 90, 141],
            width: 2,
            style: "dash",
            marker: new LineSymbolMarker({
                style: "circle",
                placement: "begin-end",
                color: [4, 90, 141],
            }),
        }),
        attributes: {
            tool: "angle",
            type: "angleLine",
            measurementId,
            pinnedStart,
        },
    });

    const gridNorthLine = new Polyline({
        paths: [
            [
                [vertices[0][0], vertices[0][1]],
                [vertices[0][0], vertices[0][1] + lineLengthMeters],
            ],
        ],
        spatialReference: mapview.spatialReference,
    });

    const centerPoint = new Point({
        x: vertices[0][0],
        y: vertices[0][1],
        spatialReference: mapview.spatialReference,
    });

    const transformation = new Transformation();
    transformation.rotate(await computeTrueNorth(centerPoint), centerPoint?.x ?? 0, centerPoint?.y ?? 0);
    const trueNorthLine = affineTransformOperator.execute(gridNorthLine, transformation);

    if (vertices.length < 2) return { graphics: [lineGraphic], measures: [] };

    const angleDrawnLine =
        Math.atan2(vertices[1][1] - vertices[0][1], vertices[1][0] - vertices[0][0]) * (180 / Math.PI);
    const angleTrueNorthLine =
        Math.atan2(
            (trueNorthLine as Polyline).paths[0][1][1] - (trueNorthLine as Polyline).paths[0][0][1],
            (trueNorthLine as Polyline).paths[0][1][0] - (trueNorthLine as Polyline).paths[0][0][0]
        ) *
        (180 / Math.PI);
    // Lyktas 0° peker motsatt vei av geografisk nord, derfor legges 180° til referanseretningen.
    const angleLighthouseZeroLine = angleTrueNorthLine + 180;
    const angleBetweenLines = (angleLighthouseZeroLine - angleDrawnLine + 360) % 360;

    const oppositeAngle = (angleBetweenLines + 180) % 360;

    const angleFromZeroText = `${angleBetweenLines.toFixed(1)}°`;
    const oppositeAngleText = `${oppositeAngle.toFixed(1)}°`;

    const textAngle = -Math.atan2(vertices[1][1] - vertices[0][1], vertices[1][0] - vertices[0][0]) * (180 / Math.PI);
    const xoffsetMeasurements = Math.sin((textAngle / 180) * Math.PI) * 12;
    const yoffsetMeasurements = Math.cos((textAngle / 180) * Math.PI) * 8;
    const angleBreakPointCondition = textAngle >= 90 || textAngle <= -90;
    const labelLengthMetersText =
        lineLengthMeters < 1000 ? `${lineLengthMeters.toFixed(2)} m` : `${(lineLengthMeters / 1000).toFixed(2)} km`;
    const labelLengthNauticalMilesText = `${lineLengthNauticalMiles.toFixed(2)} Nm`;

    const lengthText = unit === Unit.Meter ? labelLengthMetersText : labelLengthNauticalMilesText;

    const labelLengthSymbol = new TextSymbol({
        text: lengthText,
        color: "black",
        font: {
            size: 12,
            weight: "bold",
        },
        verticalAlignment: "bottom",
        horizontalAlignment: "center",
        rotated: true,
        angle: angleBreakPointCondition ? textAngle + 180 : textAngle,
        xoffset: angleBreakPointCondition ? -xoffsetMeasurements : xoffsetMeasurements,
        yoffset: angleBreakPointCondition ? -yoffsetMeasurements : yoffsetMeasurements,
    });

    const labelLengthPoint = new Point({
        x: (vertices[0][0] + vertices[1][0]) / 2,
        y: (vertices[0][1] + vertices[1][1]) / 2,
        spatialReference: mapview.spatialReference,
    });

    const labelLength = new Graphic({
        geometry: labelLengthPoint,
        symbol: labelLengthSymbol,
        attributes: {
            tool: "angle",
            type: "lengthLabel",
            measurementId,
            lengthMeters: lineLengthMeters,
            lengthNauticalMiles: lineLengthNauticalMiles,
        },
    });

    const angleText = `${angleFromZeroText} / ${oppositeAngleText}`;

    const labelAngleSymbol = new TextSymbol({
        text: angleText,
        color: "black",
        font: {
            size: 12,
            weight: "bold",
        },
        verticalAlignment: "top",
        horizontalAlignment: "center",
        rotated: true,
        angle: angleBreakPointCondition ? textAngle + 180 : textAngle,
        xoffset: angleBreakPointCondition ? xoffsetMeasurements : -xoffsetMeasurements,
        yoffset: angleBreakPointCondition ? yoffsetMeasurements : -yoffsetMeasurements,
    });

    const labelAngle = new Graphic({
        geometry: labelLengthPoint,
        symbol: labelAngleSymbol,
        attributes: {
            tool: "angle",
            type: "angleLabel",
            measurementId,
            lengthMeters: lineLengthMeters,
            lengthNauticalMiles: lineLengthNauticalMiles,
            angleDegrees: angleBetweenLines,
            oppositeAngleDegrees: oppositeAngle,
        },
    });

    return {
        graphics: [lineGraphic, labelLength, labelAngle],
        measures: [
            { key: "Lengde", value: lengthText },
            { key: "Retning 1", value: angleFromZeroText },
            { key: "Retning 2", value: oppositeAngleText },
        ],
    };
};

/** Draws an angle into the scratch layer while it is being placed. */
export const drawAngle = async (
    vertices: number[][],
    setCurrentMeasures: Function,
    mapview: MapView,
    unit: string | null,
    pinnedStart: number[] | null = null
) => {
    let angleGraphicsLayer = mapview.map?.findLayerById("angleGraphicsLayer") as GraphicsLayer;
    if (!angleGraphicsLayer) {
        angleGraphicsLayer = new GraphicsLayer({
            id: "angleGraphicsLayer",
            title: "Vinkelverktøy",
            listMode: "hide",
        });
        mapview.map?.add(angleGraphicsLayer);
    }

    angleGraphicsLayer.removeAll();

    const { graphics, measures } = await buildAngleGraphics(vertices, mapview, unit, pinnedStart);

    angleGraphicsLayer.addMany(graphics);

    if (measures.length > 0) {
        setCurrentMeasures(measures);
    }
};
